import { useState } from 'react';
import { Banner, Box, Button, Field, Group, Input, Text } from '@optiaxiom/react';
import { IconCircleCheckSolid } from '@optiaxiom/icons';
import { AppDialog } from '@/components/ui/AppDialog';
import { ApiError, useRegisterApp, type RegisteredApp } from '@/lib/api';
import { validateAppId } from './validateAppId';
import styles from './RegisterAppModal.module.css';

interface RegisterAppModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful registration, with the created app. */
  onRegistered: (app: RegisteredApp) => void;
}

type TargetProduct = 'OCP' | 'ODP';

const PRODUCTS: ReadonlyArray<{
  value: TargetProduct;
  iconClass: string;
  title: string;
  subtitle: string;
  description: string;
}> = [
  {
    value: 'OCP',
    iconClass: styles.iconOcp,
    title: 'For OCP',
    subtitle: 'Optimizely Connect Platform',
    description:
      'Build apps that extend Optimizely and connect it to external systems — pull data in, push it out, or add tools to Graph, CMS, and Mark.',
  },
  {
    value: 'ODP',
    iconClass: styles.iconOdp,
    title: 'For ODP',
    subtitle: 'Optimizely Data Platform',
    description:
      'Connect customer data to ODP and activate it — sync sources in, and send campaigns out across marketing platforms',
  },
];

/** Maps a failed registration to a user-facing message. */
function toErrorMessage(error: Error): string {
  if (error instanceof ApiError) {
    if (error.status === 409) {
      return 'An app with this ID already exists — app IDs must be globally unique.';
    }
    if (error.status === 400) {
      return 'Please check the app ID and name, then try again.';
    }
    if (error.status === 401 || error.status === 403) {
      return 'You don’t have permission to register an app.';
    }
  }
  return 'Something went wrong registering your app. Please try again.';
}

/**
 * Register-app form: app id + name + the OCP/ODP target picker. Submits to
 * POST /dev-portal/apps; on success the apps list is refreshed and the dialog closes.
 */
export function RegisterAppModal({ open, onClose, onRegistered }: RegisterAppModalProps) {
  const [name, setName] = useState('');
  const [appId, setAppId] = useState('');
  const [target, setTarget] = useState<TargetProduct>('OCP');
  const register = useRegisterApp();

  const trimmedAppId = appId.trim();
  // Client-side format check (mirrors the OCP CLI rules) shown inline on the field.
  const appIdFormatError = trimmedAppId.length > 0 ? validateAppId(trimmedAppId) : undefined;
  const canSubmit = trimmedAppId.length > 0 && !appIdFormatError && name.trim().length > 0;

  // A 409 is specifically about the app id (already taken). Field-level error precedence:
  // client-side format first, then the duplicate from the server; everything else (generic
  // 400 / permission / network) stays in the banner.
  const duplicateAppId = register.error instanceof ApiError && register.error.status === 409;
  const appIdError =
    appIdFormatError ??
    (duplicateAppId ? 'This app ID is already taken. App IDs must be globally unique.' : undefined);

  // Clear a previous failure as soon as the user edits, so stale errors don't linger.
  const clearErrorOnEdit = () => {
    if (register.isError) register.reset();
  };

  const resetForm = () => {
    setName('');
    setAppId('');
    setTarget('OCP');
    register.reset();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = () => {
    register.mutate(
      { appId: appId.trim(), name: name.trim(), accountType: target },
      {
        onSuccess: (app) => {
          resetForm();
          onRegistered(app);
        },
      },
    );
  };

  return (
    <AppDialog
      open={open}
      onClose={register.isPending ? () => {} : handleClose}
      title="Register a new app"
      footer={
        <>
          <Button appearance="subtle" onClick={handleClose} disabled={register.isPending}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={handleSubmit}
            loading={register.isPending}
            disabled={!canSubmit || register.isPending}
          >
            Register app
          </Button>
        </>
      }
    >
      <Group flexDirection="column" gap="16" alignItems="stretch">
        <Banner intent="information" rounded="lg">
          <Box asChild className={styles.bullets}>
            <ul>
              <li>The app ID has to be globally unique.</li>
              <li>The app will be shared by default with the rest of the developers in your org.</li>
            </ul>
          </Box>
        </Banner>

        {register.isError && !duplicateAppId && (
          <Banner intent="danger" rounded="lg">
            <Text>{toErrorMessage(register.error)}</Text>
          </Banner>
        )}

        <Field
          label="App ID"
          required
          description="Lowercase letters, numbers and underscores; must start with a letter; 3–32 characters."
          error={appIdError}
        >
          <Input
            value={appId}
            onChange={(event) => {
              setAppId(event.target.value);
              clearErrorOnEdit();
            }}
            placeholder="e.g. klaviyo_connector"
          />
        </Field>

        <Field label="App display name" required>
          <Input
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              clearErrorOnEdit();
            }}
            placeholder="e.g. Klaviyo Connector"
          />
        </Field>

        <Group flexDirection="column" gap="8" alignItems="stretch" role="radiogroup">
          <Text fontWeight="600">What are you building for?</Text>
          {PRODUCTS.map((product) => {
            const selected = target === product.value;
            return (
              <Box
                key={product.value}
                role="radio"
                aria-checked={selected}
                tabIndex={0}
                onClick={() => setTarget(product.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setTarget(product.value);
                  }
                }}
                className={styles.productCard}
                rounded="lg"
                p="12"
                border="1"
                borderColor="border.default"
                bg={selected ? 'bg.secondary' : undefined}
              >
                <Group flexDirection="row" justifyContent="space-between" alignItems="start" gap="12">
                  <Group flexDirection="row" gap="12" alignItems="start">
                    <Box className={`${styles.productIcon} ${product.iconClass}`}>{product.value}</Box>
                    <Group flexDirection="column" gap="2" alignItems="start">
                      <Text fontWeight="700">{product.title}</Text>
                      <Text fontSize="xs" color="fg.tertiary" textTransform="uppercase">
                        {product.subtitle}
                      </Text>
                      <Text color="fg.secondary">{product.description}</Text>
                    </Group>
                  </Group>
                  {selected ? (
                    <Box asChild color="fg.success">
                      <span>
                        <IconCircleCheckSolid color='green' size={20} />
                      </span>
                    </Box>
                  ) : (
                    <Box className={styles.radioEmpty} />
                  )}
                </Group>
              </Box>
            );
          })}
        </Group>
      </Group>
    </AppDialog>
  );
}
